import React, { useEffect, useState } from "react";
import * as echarts from "echarts";
import { Box, Grid, TextField, IconButton, Typography } from "@mui/material";
import { Refresh } from "@mui/icons-material";
import dayjs from "dayjs";
import useApiCall from "../common/api-call";
import { getHistoryStatistics } from "./statistics-service";

const ExecutionStatisticsView = () => {
  const { apiCall } = useApiCall();
  const [filteredData, setFilteredData] = useState([]);

  // Initialize dates
  const today = new Date();
  const thirtyDaysAgo = new Date(today);
  thirtyDaysAgo.setDate(today.getDate() - 30);

  const [startDate, setStartDate] = useState(
    thirtyDaysAgo.toISOString().split("T")[0],
  );
  const [endDate, setEndDate] = useState(today.toISOString().split("T")[0]);

  const fetchStatisticsData = async (start = startDate, end = endDate) => {
    try {
      const response = await getHistoryStatistics(apiCall, start, end);

      // Handle different possible response structures
      if (
        response &&
        response.statistics &&
        Array.isArray(response.statistics)
      ) {
        setFilteredData(response.statistics);
      } else if (response && Array.isArray(response)) {
        // Fallback for direct array response
        setFilteredData(response);
      } else {
        console.log("Unexpected response structure:", response);
        setFilteredData([]);
      }
    } catch (error) {
      console.error("Error fetching execution statistics:", error);
      setFilteredData([]);
    }
  };

  useEffect(() => {
    fetchStatisticsData();
  }, []);

  const handleFetchData = () => {
    fetchStatisticsData(startDate, endDate);
  };

  // Calculate number of days between start and end date
  const calculateDaysBetween = (start, end) => {
    const startDateObj = new Date(start);
    const endDateObj = new Date(end);
    const timeDiff = endDateObj.getTime() - startDateObj.getTime();
    return Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1 to include both start and end dates
  };

  const daysBetween = calculateDaysBetween(startDate, endDate);

  const dayData = Array.from({ length: daysBetween }, (_, i) => {
    const date = new Date(startDate);
    date.setDate(date.getDate() + i);
    return {
      day: date.toISOString().split("T")[0],
      successCount: 0,
      errorCount: 0,
      totalCount: 0,
    };
  });

  filteredData?.forEach(item => {
    const itemDate = item.date || "";
    const dateIndex = dayData.findIndex(day => day.day === itemDate);
    if (dateIndex !== -1) {
      dayData[dateIndex].successCount += item.successCount || 0;
      dayData[dateIndex].errorCount += item.errorCount || 0;
      dayData[dateIndex].totalCount += item.totalCount || 0;
    }
  });

  // Calculate totals for summary cards
  const totalSuccessCount = dayData.reduce(
    (sum, item) => sum + item.successCount,
    0,
  );
  const totalErrorCount = dayData.reduce(
    (sum, item) => sum + item.errorCount,
    0,
  );
  const totalExecutionCount = dayData.reduce(
    (sum, item) => sum + item.totalCount,
    0,
  );
  const successRate =
    totalExecutionCount > 0
      ? ((totalSuccessCount / totalExecutionCount) * 100).toFixed(1)
      : 0;

  // Chart options for Execution Statistics
  const getExecutionChartOption = () => {
    const maxValue = Math.max(
      ...dayData.map(item =>
        Math.max(item.successCount, item.errorCount, item.totalCount),
      ),
      0,
    );
    const yAxisMax = maxValue > 0 ? Math.ceil(maxValue * 1.1) : 50;

    return {
      title: {
        text: "Automation Execution Statistics",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name } = params[0];
          let tooltipContent = `Date: ${name}<br />`;
          params.forEach(param => {
            tooltipContent += `${
              param.seriesName
            }: ${param.value.toLocaleString()}<br />`;
          });
          return tooltipContent;
        },
      },
      legend: {
        data: ["Total Executions", "Success Count", "Error Count"],
        top: "12%",
      },
      grid: {
        top: "30%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: yAxisMax,
        axisLabel: {
          formatter: value => Math.round(value).toLocaleString(),
          padding: [5, 5, 5, 5],
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: "Total Executions",
          type: "line",
          data: dayData.map(item => item.totalCount),
          animation: true,
          lineStyle: {
            width: 3,
          },
          itemStyle: {
            color: "#2196f3",
          },
          areaStyle: {
            opacity: 0.1,
            color: "#2196f3",
          },
        },
        {
          name: "Success Count",
          type: "line",
          data: dayData.map(item => item.successCount),
          animation: true,
          lineStyle: {
            width: 2,
          },
          itemStyle: {
            color: "#4caf50",
          },
        },
        {
          name: "Error Count",
          type: "line",
          data: dayData.map(item => item.errorCount),
          animation: true,
          lineStyle: {
            width: 2,
          },
          itemStyle: {
            color: "#f44336",
          },
        },
      ],
    };
  };

  // Chart options for Success Rate
  const getSuccessRateChartOption = () => {
    const successRateData = dayData.map(item => {
      if (item.totalCount === 0) return 0;
      return (item.successCount / item.totalCount) * 100;
    });

    const maxRate = Math.max(...successRateData, 0);
    const rateYAxisMax = maxRate > 0 ? Math.ceil(maxRate * 1.1) : 100;

    return {
      title: {
        text: "Success Rate (%)",
        left: "center",
        textStyle: {
          fontSize: 16,
          fontWeight: "bold",
        },
      },
      tooltip: {
        trigger: "axis",
        formatter: params => {
          const { name, value } = params[0];
          return `Date: ${name}<br />Success Rate: ${value.toFixed(2)}%`;
        },
      },
      grid: {
        top: "25%",
        left: "15%",
        right: "10%",
        bottom: "15%",
      },
      xAxis: {
        type: "category",
        data: dayData.map(item => item.day),
        axisLabel: {
          interval: Math.max(1, Math.floor(daysBetween / 10)),
          rotate: 45,
          margin: 10,
          formatter: value => dayjs(value).format("MM-DD"),
          showMaxLabel: true,
        },
        axisTick: {
          alignWithLabel: true,
          interval: Math.max(1, Math.floor(daysBetween / 15)),
        },
      },
      yAxis: {
        type: "value",
        min: 0,
        max: rateYAxisMax,
        axisLabel: {
          formatter: value => `${Math.round(value)}%`,
          padding: [5, 5, 5, 5],
        },
        axisLine: {
          show: true,
        },
        axisTick: {
          show: true,
        },
        splitLine: {
          show: true,
          lineStyle: {
            type: "dashed",
            color: "#ccc",
          },
        },
      },
      series: [
        {
          name: "Success Rate",
          type: "line",
          data: successRateData,
          animation: true,
          lineStyle: {
            width: 3,
          },
          itemStyle: {
            color: "#00bcd4",
          },
          areaStyle: {
            opacity: 0.3,
            color: "#00bcd4",
          },
        },
      ],
    };
  };

  useEffect(() => {
    // Initialize main chart
    const executionChartDom = document.getElementById("execution-chart");
    if (executionChartDom) {
      const executionChart = echarts.init(executionChartDom);
      executionChart.setOption(getExecutionChartOption());

      // Initialize success rate chart
      const successRateChartDom = document.getElementById("success-rate-chart");
      if (successRateChartDom) {
        const successRateChart = echarts.init(successRateChartDom);
        successRateChart.setOption(getSuccessRateChartOption());

        const handleResize = () => {
          executionChart.resize();
          successRateChart.resize();
        };

        window.addEventListener("resize", handleResize);

        return () => {
          executionChart.dispose();
          successRateChart.dispose();
          window.removeEventListener("resize", handleResize);
        };
      }
    }
  }, [filteredData, startDate, endDate]);

  return (
    <Box sx={{ flexGrow: 1, height: "100%", m: 2, pb: "80px" }}>
      {/* Date Selection Controls */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#f9f9f9",
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: "#333" }}>
          Date Range Selection
        </Typography>
        <Grid container spacing={2} alignItems="end">
          <Grid item xs={12} sm={4}>
            <TextField
              label="Start Date"
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid item xs={12} sm={4}>
            <TextField
              label="End Date"
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
              fullWidth
              size="small"
            />
          </Grid>
          <Grid
            item
            xs={12}
            sm={4}
            sx={{
              display: "flex",
              justifyContent: "flex-start",
              alignItems: "end",
            }}
          >
            <IconButton
              onClick={handleFetchData}
              sx={{
                backgroundColor: "#1976d2",
                color: "white",
                "&:hover": {
                  backgroundColor: "#1565c0",
                },
                width: "40px",
                height: "40px",
                borderRadius: "8px",
                ml: 1,
              }}
            >
              <Refresh fontSize="small" />
            </IconButton>
          </Grid>
        </Grid>
      </Box>

      {/* Summary Cards */}
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          mb: 3,
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Total Executions
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#2196f3" }}>
            {totalExecutionCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Success Count
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#4caf50" }}>
            {totalSuccessCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Error Count
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#f44336" }}>
            {totalErrorCount.toLocaleString()}
          </Box>
        </Box>
        <Box
          sx={{
            borderRadius: "8px",
            padding: "12px",
            backgroundColor: "#fff",
            boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
            flex: {
              xs: "1 1 calc(50% - 16px)",
              sm: "1 1 calc(25% - 16px)",
            },
            maxWidth: "250px",
            minWidth: "150px",
            textAlign: "center",
          }}
        >
          <Box sx={{ fontSize: "14px", fontWeight: "bold", color: "#333" }}>
            Success Rate
          </Box>
          <Box sx={{ fontSize: "24px", fontWeight: "bold", color: "#00bcd4" }}>
            {successRate}%
          </Box>
        </Box>
      </Box>

      {/* Charts in Column Layout */}
      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ width: "100%", height: { xs: "350px", sm: "450px" } }}>
            <Box
              id="execution-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        </Grid>
        <Grid item xs={12}>
          <Box sx={{ width: "100%", height: { xs: "350px", sm: "450px" } }}>
            <Box
              id="success-rate-chart"
              style={{ width: "100%", height: "100%" }}
            />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
};

export default ExecutionStatisticsView;
